import { LitElement, html, css } from "https://cdn.jsdelivr.net/npm/lit-element@4.1.1/+esm";

/**
 * Detects the current tenant UI type by inspecting loaded stylesheets.
 * @returns {'legadom'|'antilles'}
 */
function detectUIType() {
	const links = document.querySelectorAll('link[rel="stylesheet"]');
	for (const link of links) {
		const href = link.getAttribute('href') || '';
		if (href.includes('/legadom/')) return 'legadom';
	}
	return 'antilles';
}

/**
 * Returns the theme color tokens for the given UI type.
 * @param {'legadom'|'antilles'} uiType
 */
function getTheme(uiType) {
	if (uiType === 'legadom') {
		return {
			headerBg:    '#111955',
			headerColor: '#ffffff',
			accentColor: '#111955',
		};
	}
	return {
		headerBg:    '#03879e',
		headerColor: '#ffffff',
		accentColor: '#03879e',
	};
}

/** Trash icon (heroicons solid, 20×20). */
const TRASH_ICON = html`
	<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
		<path fill-rule="evenodd"
			d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2
			  0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7
			  8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102
			  0V8a1 1 0 00-1-1z"
			clip-rule="evenodd"/>
	</svg>`;

/**
 * <public-confirm-modal> — Confirmation dialog for public-facing pages.
 *
 * Follows the <alert-modal> design language (white card, tenant-aware header)
 * with confirm/cancel buttons and a danger accent for destructive actions.
 *
 * Usage (Promise API):
 *   const confirmed = await modal.confirm({
 *     title:        'Supprimer le brouillon',
 *     message:      'Voulez-vous vraiment supprimer ce brouillon ?',
 *     confirmLabel: 'Supprimer',
 *     cancelLabel:  'Annuler',
 *   });
 *   if (confirmed) { form.submit(); }
 *
 * @fires confirm — user clicked the confirm button
 * @fires cancel  — user clicked cancel, backdrop, or pressed Escape
 */
export class PublicConfirmModal extends LitElement {

	static properties = {
		open:         { type: Boolean, reflect: true },
		title:        { type: String },
		message:      { type: String },
		confirmLabel: { type: String },
		cancelLabel:  { type: String },
	};

	constructor() {
		super();
		this.open         = false;
		this.title        = 'Confirmer';
		this.message      = '';
		this.confirmLabel = 'Confirmer';
		this.cancelLabel  = 'Annuler';
		this._resolve     = null;
		this._theme       = getTheme(detectUIType());
	}

	/**
	 * Opens the dialog and returns a Promise<boolean>.
	 * @param {{ title?, message, confirmLabel?, cancelLabel? }} opts
	 * @returns {Promise<boolean>}
	 */
	confirm(opts = {}) {
		this.title        = opts.title        || 'Confirmer';
		this.message      = opts.message      || '';
		this.confirmLabel = opts.confirmLabel  || 'Confirmer';
		this.cancelLabel  = opts.cancelLabel   || 'Annuler';
		this.open = true;
		return new Promise(function (resolve) { this._resolve = resolve; }.bind(this));
	}

	_confirm() {
		this.open = false;
		if (this._resolve) { this._resolve(true); this._resolve = null; }
		this.dispatchEvent(new CustomEvent('confirm', { bubbles: true, composed: true }));
	}

	_cancel() {
		this.open = false;
		if (this._resolve) { this._resolve(false); this._resolve = null; }
		this.dispatchEvent(new CustomEvent('cancel', { bubbles: true, composed: true }));
	}

	_backdropClick(e) {
		if (e.target === e.currentTarget) this._cancel();
	}

	connectedCallback() {
		super.connectedCallback();
		this._keyHandler = function (e) { if (this.open && e.key === 'Escape') this._cancel(); }.bind(this);
		document.addEventListener('keydown', this._keyHandler);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('keydown', this._keyHandler);
	}

	static get styles() {
		return css`
			:host {
				display: none;
			}
			:host([open]) {
				display: block;
			}

			/* ── Backdrop ── */
			.backdrop {
				position: fixed;
				inset: 0;
				background: rgba(0, 0, 0, 0.45);
				display: flex;
				align-items: center;
				justify-content: center;
				z-index: 9999;
				animation: fadeIn 0.18s ease;
			}

			/* ── Card ── */
			.card {
				background: #ffffff;
				border-radius: 16px;
				box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22);
				max-width: 440px;
				width: 90%;
				overflow: hidden;
				animation: slideDown 0.24s cubic-bezier(0.34, 1.46, 0.64, 1);
			}

			/* ── Header ── */
			.header {
				display: flex;
				align-items: center;
				gap: 12px;
				padding: 18px 24px;
			}
			.header-icon {
				flex-shrink: 0;
				width: 36px;
				height: 36px;
				background: rgba(255, 255, 255, 0.25);
				border-radius: 50%;
				display: flex;
				align-items: center;
				justify-content: center;
			}
			.header-icon svg {
				width: 18px;
				height: 18px;
				fill: currentColor;
			}
			.header-title {
				font-family: 'Montserrat', 'Segoe UI', Arial, sans-serif;
				font-size: 1.05rem;
				font-weight: 700;
				letter-spacing: 0.02em;
				margin: 0;
			}

			/* ── Body ── */
			.body {
				padding: 22px 24px 8px;
				font-family: 'Segoe UI', Arial, sans-serif;
				font-size: 0.97rem;
				color: #333;
				line-height: 1.55;
			}
			.body p { margin: 0; }

			/* ── Divider ── */
			.divider {
				margin: 18px 24px 0;
				border: none;
				border-top: 1px solid #e5e7eb;
			}

			/* ── Footer ── */
			.footer {
				padding: 16px 24px 22px;
				display: flex;
				justify-content: flex-end;
				gap: 10px;
			}

			/* ── Buttons ── */
			button {
				border: none;
				border-radius: 30px;
				padding: 10px 26px;
				font-size: 0.85rem;
				font-weight: 700;
				font-family: 'Montserrat', 'Segoe UI', Arial, sans-serif;
				letter-spacing: 0.06em;
				text-transform: uppercase;
				cursor: pointer;
				transition: background 0.15s ease, transform 0.1s ease;
			}
			button:active { transform: scale(0.97); }
			button:focus-visible {
				outline: 2px solid rgba(0, 0, 0, 0.3);
				outline-offset: 2px;
			}

			.btn-cancel {
				background: #f3f4f6;
				color: #374151;
				border: 1px solid #d1d5db;
			}
			.btn-cancel:hover {
				background: #e5e7eb;
				color: #1f2937;
			}

			.btn-confirm {
				background: #dc2626;
				color: #ffffff;
			}
			.btn-confirm:hover {
				background: #b91c1c;
			}

			/* ── Animations ── */
			@keyframes fadeIn {
				from { opacity: 0; }
				to   { opacity: 1; }
			}
			@keyframes slideDown {
				from { transform: translateY(-20px) scale(0.96); opacity: 0; }
				to   { transform: translateY(0)     scale(1);    opacity: 1; }
			}
		`;
	}

	render() {
		var t = this._theme;

		return html`
			<style>
				.header {
					background: ${t.headerBg} !important;
					color: ${t.headerColor} !important;
				}
			</style>

			<div class="backdrop"
				 @click="${this._backdropClick}"
				 role="dialog"
				 aria-modal="true"
				 aria-labelledby="pcm-title"
				 aria-describedby="pcm-msg">
				<div class="card">

					<div class="header">
						<div class="header-icon">${TRASH_ICON}</div>
						<p id="pcm-title" class="header-title">${this.title}</p>
					</div>

					<div class="body">
						<p id="pcm-msg">${this.message}</p>
					</div>

					<hr class="divider">

					<div class="footer">
						<button class="btn-cancel"
								@click="${this._cancel}">
							${this.cancelLabel}
						</button>
						<button class="btn-confirm"
								@click="${this._confirm}"
								autofocus>
							${this.confirmLabel}
						</button>
					</div>

				</div>
			</div>
		`;
	}
}

customElements.define('public-confirm-modal', PublicConfirmModal);
